/**
 * MfaEnrollmentHandoff — the marketing apex's held sign-in (N152, MFA-SETUP-CONVERGE).
 *
 * Every tenant sign-in holds a person whose MFA an administrator's reset cleared (403 `MFA_SETUP_REQUIRED`, owner ruling
 * Q-ARG-1). The tenant login enrols them on the spot (`<MfaEnrollment>`). The apex cannot: a completed enrolment stores
 * its session on the MARKETING origin, and the apex's session handoff needs an access token the held person does not
 * have. So the apex hands them to their company's own sign-in page, the shape the shared-terminal handoff already uses
 * (`<sub>.<app>/login`): it says why, in the catalog's words, and links there. "Back" returns to the sign-in form.
 *
 * @module @puffin-core/login-shell/components/MfaEnrollmentHandoff
 */

import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLoginTranslate } from '../hooks/useLoginTranslate.js';

/**
 * @param {Object} props
 * @param {string} props.tenantLoginUrl - the company's own sign-in page
 * @param {() => void} props.onBack - return to the sign-in form
 * @param {Function} [props.t] - the app's translator (see `useLoginTranslate`)
 * @returns {React.JSX.Element}
 */
export function MfaEnrollmentHandoff({ tenantLoginUrl, onBack, t }) {
  const translate = useLoginTranslate(t);
  const continueRef = useRef(null);
  // The step replaces the form the person just submitted: focus moves to its one forward action.
  useEffect(() => {
    continueRef.current.focus();
  }, []);

  return (
    <div data-testid="mfa-enrollment-handoff">
      <h2 className="mfa-challenge-title">{translate('login.mfaEnrollmentHandoff.title')}</h2>
      <p className="mfa-challenge-subtitle" role="status">
        {translate('login.mfaEnrollmentHandoff.body')}
      </p>
      <div className="mfa-setup-actions">
        <button type="button" className="btn btn-secondary" onClick={onBack}>
          {translate('login.mfaEnrollmentHandoff.back')}
        </button>
        <a ref={continueRef} className="btn btn-primary" href={tenantLoginUrl}>
          {translate('login.mfaEnrollmentHandoff.continue')}
        </a>
      </div>
    </div>
  );
}

MfaEnrollmentHandoff.propTypes = {
  tenantLoginUrl: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  t: PropTypes.func,
};

export default MfaEnrollmentHandoff;
